import { useState, useCallback, useRef, useEffect } from "react";
import { useLocation, useParams } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { NavHeader } from "@/components/layout/nav-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { v4 as uuidv4 } from "uuid";
import ReactPlayer from "react-player";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Download, GripVertical, Share2, Copy, Check, Loader2, AlertCircle, Play, Pencil, Plus, Trash2 } from "lucide-react";

// --- Timestamp parsing (same logic as VideoReviewPage) ---

function timestampToSeconds(ts: string): number {
  const parts = ts.split(':').map(Number);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function parseAndNormalizeTimestamp(raw: string): { normalized: string; error: string | null } {
  const trimmed = raw.trim();
  if (trimmed === "") return { normalized: "", error: null };

  if (/[^0-9:]/.test(trimmed)) {
    return { normalized: trimmed, error: "Use m:ss (6:45) or h:mm:ss (1:06:45)" };
  }

  if (trimmed.includes("::") || trimmed.endsWith(":")) {
    return { normalized: trimmed, error: "Use m:ss (6:45) or h:mm:ss (1:06:45)" };
  }

  if (trimmed.includes(":")) {
    const parts = trimmed.split(":");

    if (parts.length === 2) {
      const minStr = parts[0];
      const secStr = parts[1];
      if (secStr.length !== 2) return { normalized: trimmed, error: "Seconds must be 2 digits (e.g. :06)" };
      const minutes = minStr === "" ? 0 : parseInt(minStr, 10);
      const seconds = parseInt(secStr, 10);
      if (isNaN(minutes) || isNaN(seconds)) return { normalized: trimmed, error: "Use m:ss (6:45) or h:mm:ss (1:06:45)" };
      if (seconds < 0 || seconds > 59) return { normalized: trimmed, error: "Seconds must be 0-59" };
      if (minutes < 0) return { normalized: trimmed, error: "Minutes must be non-negative" };
      return { normalized: `${minutes}:${String(seconds).padStart(2, "0")}`, error: null };
    }

    if (parts.length === 3) {
      const hourStr = parts[0];
      const minStr = parts[1];
      const secStr = parts[2];
      if (minStr.length !== 2) return { normalized: trimmed, error: "Minutes must be 2 digits with hours (e.g. 1:06:45)" };
      if (secStr.length !== 2) return { normalized: trimmed, error: "Seconds must be 2 digits (e.g. 1:06:45)" };
      const hours = parseInt(hourStr, 10);
      const minutes = parseInt(minStr, 10);
      const seconds = parseInt(secStr, 10);
      if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) return { normalized: trimmed, error: "Use m:ss (6:45) or h:mm:ss (1:06:45)" };
      if (seconds < 0 || seconds > 59) return { normalized: trimmed, error: "Seconds must be 0-59" };
      if (minutes < 0 || minutes > 59) return { normalized: trimmed, error: "Minutes must be 0-59 with hours" };
      if (hours < 0) return { normalized: trimmed, error: "Hours must be non-negative" };
      return { normalized: `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`, error: null };
    }

    return { normalized: trimmed, error: "Use m:ss (6:45) or h:mm:ss (1:06:45)" };
  }

  const seconds = parseInt(trimmed, 10);
  if (isNaN(seconds) || seconds < 0) return { normalized: trimmed, error: "Use m:ss (6:45) or h:mm:ss (1:06:45)" };
  return { normalized: `0:${String(seconds).padStart(2, "0")}`, error: null };
}

// --- Types ---

interface KeyMomentUI {
  clientId: string;
  timestamp: string;
  label: string;
  timestampError?: string;
  timestampEnd: string;
  timestampEndError?: string;
  endMode: "custom" | "30s" | "60s";
}

// --- Sortable Key Moment Component (matches review page) ---

function SortableKeyMoment({
  km,
  index,
  onTimestampChange,
  onTimestampBlur,
  onTimestampEndChange,
  onTimestampEndBlur,
  onEndModeChange,
  onLabelChange,
}: {
  km: KeyMomentUI;
  index: number;
  onTimestampChange: (clientId: string, value: string) => void;
  onTimestampBlur: (clientId: string) => void;
  onTimestampEndChange: (clientId: string, value: string) => void;
  onTimestampEndBlur: (clientId: string) => void;
  onEndModeChange: (clientId: string, mode: "custom" | "30s" | "60s") => void;
  onLabelChange: (clientId: string, timestamp: string, value: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: km.clientId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing text-gray-500 hover:text-gray-300 touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <h4 className="text-sm font-medium text-gray-300">Timestamp {index + 1}</h4>
      </div>
      <div className="pl-6 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Timestamp Start */}
          <div className="flex-shrink-0">
            <label htmlFor={`ts-${km.clientId}`} className="block text-xs text-gray-400 mb-1">Start</label>
            <Input
              id={`ts-${km.clientId}`}
              type="text"
              value={km.timestamp}
              onChange={(e) => onTimestampChange(km.clientId, e.target.value.replace(/[^0-9:]/g, ""))}
              onBlur={() => onTimestampBlur(km.clientId)}
              placeholder="h:mm:ss"
              className={`bg-[#1a1a1a] border text-white placeholder:text-gray-500 w-28 text-sm tabular-nums rounded-md ${km.timestampError ? "border-red-500" : "border-white/10"}`}
              maxLength={10}
            />
            {km.timestampError && (
              <p className="text-xs text-red-400 mt-1">{km.timestampError}</p>
            )}
          </div>

          {/* Duration / End */}
          <div className="flex-shrink-0">
            <label className="block text-xs text-gray-400 mb-1">Duration</label>
            <div className="flex items-center gap-1.5 h-10 bg-[#1a1a1a] border border-white/10 rounded-md px-1">
              <button
                type="button"
                onClick={() => onEndModeChange(km.clientId, "30s")}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                  km.endMode === "30s"
                    ? "bg-white text-black"
                    : "text-white/40 hover:text-white/70"
                }`}
              >
                30s
              </button>
              <button
                type="button"
                onClick={() => onEndModeChange(km.clientId, "60s")}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                  km.endMode === "60s"
                    ? "bg-white text-black"
                    : "text-white/40 hover:text-white/70"
                }`}
              >
                60s
              </button>
              <button
                type="button"
                onClick={() => onEndModeChange(km.clientId, "custom")}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                  km.endMode === "custom"
                    ? "bg-white text-black"
                    : "text-white/40 hover:text-white/70"
                }`}
              >
                Custom
              </button>
            </div>
          </div>

          {/* Custom End input — only visible when Custom is selected */}
          {km.endMode === "custom" && (
            <div className="flex-shrink-0">
              <label htmlFor={`ts-end-${km.clientId}`} className="block text-xs text-gray-400 mb-1">End</label>
              <Input
                id={`ts-end-${km.clientId}`}
                type="text"
                value={km.timestampEnd}
                onChange={(e) => onTimestampEndChange(km.clientId, e.target.value.replace(/[^0-9:]/g, ""))}
                onBlur={() => onTimestampEndBlur(km.clientId)}
                placeholder="h:mm:ss"
                className={`bg-[#1a1a1a] border text-white placeholder:text-gray-500 w-28 text-sm tabular-nums rounded-md ${km.timestampEndError ? "border-red-500" : "border-white/10"}`}
                maxLength={10}
              />
            </div>
          )}

          {/* Track */}
          <div className="flex-grow">
            <label htmlFor={`track-${km.clientId}`} className="block text-xs text-gray-400 mb-1">Track</label>
            <Input
              id={`track-${km.clientId}`}
              type="text"
              value={km.label}
              onChange={(e) => onLabelChange(km.clientId, km.timestamp, e.target.value)}
              placeholder="Type here..."
              className="bg-[#1a1a1a] border border-white/10 text-white placeholder:text-gray-500 w-full"
            />
          </div>
        </div>
        {km.timestampEndError && (
          <p className="text-xs text-red-400">{km.timestampEndError}</p>
        )}
      </div>
    </div>
  );
}

// --- Clip status badge ---

function getClipStatusBadge(status: string) {
  switch (status) {
    case "queued":
      return <Badge variant="secondary" className="bg-gray-600 text-gray-200">Queued</Badge>;
    case "processing":
      return (
        <Badge variant="secondary" className="bg-amber-600/20 text-amber-400 border-amber-600/30">
          <Loader2 className="h-3 w-3 animate-spin mr-1" />
          Processing
        </Badge>
      );
    case "done":
      return <Badge variant="secondary" className="bg-green-600/20 text-green-400 border-green-600/30">Done</Badge>;
    case "failed":
      return <Badge variant="destructive">Failed</Badge>;
    case "building":
      return (
        <Badge variant="secondary" className="bg-amber-600/20 text-amber-400 border-amber-600/30">
          <Loader2 className="h-3 w-3 animate-spin mr-1" />
          Building
        </Badge>
      );
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

// --- Page Component ---

export default function PrivateRecordingDetailPage() {
  const [, navigate] = useLocation();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const playerRef = useRef<any>(null);
  const [isVideoStarted, setIsVideoStarted] = useState(false);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [showShareModal, setShowShareModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [enableVideoDownload, setEnableVideoDownload] = useState(false);
  const [enableAudioDownload, setEnableAudioDownload] = useState(false);
  const [enableTimestampDownloads, setEnableTimestampDownloads] = useState(false);
  const [shareToken, setShareToken] = useState<string | null>(null);
  // Track saved permission state to detect changes
  const savedPermissions = useRef({ video: false, audio: false, timestamps: false });
  const tabRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<"timestamps" | "clips">("timestamps");
  const switchTab = (tab: "timestamps" | "clips") => {
    setActiveTab(tab);
    requestAnimationFrame(() => {
      tabRef.current?.scrollIntoView({ block: "start", behavior: "instant" });
    });
  };

  const recordingId = id || "1";

  // Fetch recording detail
  const { data, isLoading } = useQuery<{
    recording: any;
    timestamps: any[];
    shareLinks: any[];
  }>({
    queryKey: [`/api/admin/private-recordings/${recordingId}`],
  });

  const recording = data?.recording;

  // Fetch clips
  const { data: clipsData } = useQuery<{
    clips: any[];
    bundle: any | null;
  }>({
    queryKey: [`/api/admin/recording-clips?privateRecordingId=${recordingId}`],
    refetchInterval: (query) => {
      const d = query.state.data as any;
      if (!d?.clips) return false;
      const hasActive = d.clips.some((c: any) => c.status === "queued" || c.status === "processing");
      const bundleActive = d.bundle && (d.bundle.status === "queued" || d.bundle.status === "building");
      return hasActive || bundleActive ? 5000 : false;
    },
  });

  // Inline title editing
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [title, setTitle] = useState("");
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Sync title from query data
  useEffect(() => {
    if (recording) {
      const t = recording.title || recording.fileName || "";
      setTitle(t);
      setEditedTitle(t);
    }
  }, [recording]);

  // Load existing share link state
  useEffect(() => {
    if (data?.shareLinks?.length) {
      const link = data.shareLinks[0];
      setShareToken(link.token);
      setEnableVideoDownload(link.enableVideoDownload);
      setEnableAudioDownload(link.enableAudioDownload);
      setEnableTimestampDownloads(link.enableTimestampDownloads);
      savedPermissions.current = {
        video: link.enableVideoDownload,
        audio: link.enableAudioDownload,
        timestamps: link.enableTimestampDownloads,
      };
    }
  }, [data?.shareLinks]);

  // Title rename mutation
  const renameMutation = useMutation({
    mutationFn: async (newTitle: string) => {
      const res = await fetch(`/api/admin/private-recordings/${recordingId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: newTitle }),
      });
      if (!res.ok) throw new Error("Failed to update title");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/admin/private-recordings/${recordingId}`] });
    },
  });

  // Timestamp save mutation
  const saveTimestampsMutation = useMutation({
    mutationFn: async (timestamps: any[]) => {
      const res = await fetch("/api/admin/recording-timestamps", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ privateRecordingId: Number(recordingId), timestamps }),
      });
      if (!res.ok) throw new Error("Failed to save timestamps");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/admin/private-recordings/${recordingId}`] });
      queryClient.invalidateQueries({ queryKey: [`/api/admin/recording-clips?privateRecordingId=${recordingId}`] });
      switchTab("clips");
      toast({ title: "Timestamps saved — clips are being generated" });
    },
  });

  // Share link mutation
  const shareLinkMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/admin/share-links", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          privateRecordingId: Number(recordingId),
          enableVideoDownload,
          enableAudioDownload,
          enableTimestampDownloads,
        }),
      });
      if (!res.ok) throw new Error("Failed to create share link");
      return res.json();
    },
    onSuccess: (data) => {
      setShareToken(data.token);
      savedPermissions.current = {
        video: enableVideoDownload,
        audio: enableAudioDownload,
        timestamps: enableTimestampDownloads,
      };
      const url = `${window.location.origin}/recording/${data.token}`;
      navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
      queryClient.invalidateQueries({ queryKey: [`/api/admin/private-recordings/${recordingId}`] });
    },
  });

  // Bundle creation mutation — returns bundle id for download tracking
  const bundleMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/admin/recording-bundles", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ privateRecordingId: Number(recordingId) }),
      });
      if (!res.ok) throw new Error("Failed to create bundle");
      return res.json() as Promise<{ id: number; status: string }>;
    },
    onSuccess: () => {
      bundleRequestedRef.current = true;
      queryClient.invalidateQueries({ queryKey: [`/api/admin/recording-clips?privateRecordingId=${recordingId}`] });
      toast({ title: "ZIP bundle queued" });
    },
  });

  const INITIAL_VISIBLE = 2;
  const MAX_TIMESTAMPS = 20;
  const ADD_INCREMENT = 1;
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  // Key moments state — initialize from loaded timestamps or empty
  const [keyMomentsUi, setKeyMomentsUi] = useState<KeyMomentUI[]>(() =>
    Array.from({ length: MAX_TIMESTAMPS }, () => ({
      clientId: uuidv4(),
      timestamp: "",
      label: "",
      timestampEnd: "",
      endMode: "60s" as const,
    }))
  );

  // Populate from loaded timestamps
  useEffect(() => {
    if (data?.timestamps?.length) {
      const loaded = data.timestamps.map((t: any) => ({
        clientId: uuidv4(),
        timestamp: t.startTime || "",
        label: t.trackTitle || "",
        timestampEnd: t.endTime || "",
        endMode: (t.durationType === "30s" ? "30s" : t.durationType === "60s" ? "60s" : "custom") as "30s" | "60s" | "custom",
      }));
      // Pad to MAX
      while (loaded.length < MAX_TIMESTAMPS) {
        loaded.push({ clientId: uuidv4(), timestamp: "", label: "", timestampEnd: "", endMode: "60s" as const });
      }
      setKeyMomentsUi(loaded);
      setVisibleCount(Math.max(INITIAL_VISIBLE, data.timestamps.length));
    }
  }, [data?.timestamps]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setKeyMomentsUi((items) => {
        const oldIndex = items.findIndex((i) => i.clientId === active.id);
        const newIndex = items.findIndex((i) => i.clientId === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }, []);

  const handleTimestampChange = useCallback((clientId: string, value: string) => {
    setKeyMomentsUi((prev) =>
      prev.map((m) =>
        m.clientId === clientId ? { ...m, timestamp: value, timestampError: undefined } : m
      )
    );
  }, []);

  const handleTimestampBlur = useCallback((clientId: string) => {
    setKeyMomentsUi((prev) =>
      prev.map((m) => {
        if (m.clientId !== clientId) return m;
        if (!m.timestamp.trim()) return m;
        const { normalized, error } = parseAndNormalizeTimestamp(m.timestamp);
        return { ...m, timestamp: normalized, timestampError: error || undefined };
      })
    );
  }, []);

  const handleLabelChange = useCallback((clientId: string, _timestamp: string, value: string) => {
    setKeyMomentsUi((prev) =>
      prev.map((m) => (m.clientId === clientId ? { ...m, label: value } : m))
    );
  }, []);

  const handleTimestampEndChange = useCallback((clientId: string, value: string) => {
    setKeyMomentsUi((prev) =>
      prev.map((m) =>
        m.clientId === clientId ? { ...m, timestampEnd: value, timestampEndError: undefined } : m
      )
    );
  }, []);

  const handleTimestampEndBlur = useCallback((clientId: string) => {
    setKeyMomentsUi((prev) =>
      prev.map((m) => {
        if (m.clientId !== clientId || m.endMode !== "custom") return m;
        if (!m.timestampEnd.trim()) return m;
        const { normalized, error } = parseAndNormalizeTimestamp(m.timestampEnd);
        if (error) return { ...m, timestampEnd: normalized, timestampEndError: error };
        if (normalized && m.timestamp.trim()) {
          const startSec = timestampToSeconds(m.timestamp);
          const endSec = timestampToSeconds(normalized);
          if (endSec <= startSec) {
            return { ...m, timestampEnd: normalized, timestampEndError: "End time must be after start time" };
          }
          if (endSec - startSec > 60) {
            return { ...m, timestampEnd: normalized, timestampEndError: "Clip cannot exceed 60 seconds" };
          }
        }
        return { ...m, timestampEnd: normalized, timestampEndError: undefined };
      })
    );
  }, []);

  const handleEndModeChange = useCallback((clientId: string, mode: "custom" | "30s" | "60s") => {
    setKeyMomentsUi((prev) =>
      prev.map((m) =>
        m.clientId === clientId ? { ...m, endMode: mode, timestampEnd: mode === "custom" ? m.timestampEnd : "", timestampEndError: undefined } : m
      )
    );
  }, []);

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const shareUrl = shareToken ? `${window.location.origin}/recording/${shareToken}` : null;
  const permissionsChanged = shareToken && (
    enableVideoDownload !== savedPermissions.current.video ||
    enableAudioDownload !== savedPermissions.current.audio ||
    enableTimestampDownloads !== savedPermissions.current.timestamps
  );

  const clips = clipsData?.clips || [];
  const bundle = clipsData?.bundle || null;

  // Auto-download ZIP when bundle finishes after user clicked "Download All ZIP"
  const bundleRequestedRef = useRef(false);
  useEffect(() => {
    if (bundleRequestedRef.current && bundle?.status === "done" && bundle?.id) {
      bundleRequestedRef.current = false;
      window.open(`/api/admin/recording-bundles/${bundle.id}/download`, "_blank");
    }
  }, [bundle?.status, bundle?.id]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-clubhouse-black text-white">
        <NavHeader />
        <div className="flex items-center justify-center pt-20">
          <Loader2 className="h-6 w-6 animate-spin text-white/30" />
        </div>
      </div>
    );
  }

  if (!recording) {
    return (
      <div className="min-h-screen bg-clubhouse-black text-white">
        <NavHeader />
        <div className="max-w-6xl mx-auto px-4 pt-20 text-center text-white/30">Recording not found.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-clubhouse-black text-white flex flex-col">
      <NavHeader />

      <main className="flex-grow">
        <div className="max-w-6xl mx-auto px-4 pt-6 pb-6">
          {/* Back + title + share */}
          <div className="flex items-center gap-4 mb-1">
            <button
              onClick={() => navigate("/admin/private-recordings")}
              className="h-8 w-8 rounded-full flex items-center justify-center text-white/40 hover:text-white hover:bg-white/[0.06] transition-colors shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            {isEditingTitle ? (
              <input
                ref={titleInputRef}
                type="text"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const trimmed = editedTitle.trim();
                    if (trimmed) {
                      setTitle(trimmed);
                      renameMutation.mutate(trimmed);
                      toast({ title: "Title updated" });
                    } else {
                      setEditedTitle(title);
                    }
                    setIsEditingTitle(false);
                  }
                  if (e.key === "Escape") {
                    setEditedTitle(title);
                    setIsEditingTitle(false);
                  }
                }}
                onBlur={() => {
                  const trimmed = editedTitle.trim();
                  if (trimmed && trimmed !== title) {
                    setTitle(trimmed);
                    renameMutation.mutate(trimmed);
                    toast({ title: "Title updated" });
                  } else {
                    setEditedTitle(title);
                  }
                  setIsEditingTitle(false);
                }}
                className="text-[28px] font-semibold tracking-tight flex-1 bg-transparent border-b-2 border-white/30 outline-none text-white min-w-0"
                autoFocus
              />
            ) : (
              <button
                onClick={() => {
                  setEditedTitle(title);
                  setIsEditingTitle(true);
                  requestAnimationFrame(() => titleInputRef.current?.focus());
                }}
                className="group flex items-center gap-3 flex-1 min-w-0 text-left"
              >
                <h2 className="text-[28px] font-semibold tracking-tight truncate">{title}</h2>
                <Pencil className="h-4 w-4 text-white/0 group-hover:text-white/30 transition-colors shrink-0" />
              </button>
            )}
            <Button
              onClick={() => setShowShareModal(true)}
              variant="outline"
              className="border-white/[0.12] text-white hover:bg-white/[0.06] font-medium px-5 rounded-full shrink-0"
            >
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </Button>
          </div>
          <p className="text-[14px] text-white/30 ml-12 mt-1">
            Uploaded by {recording.uploaderDisplayName || recording.uploaderEmail}
          </p>
        </div>

        {/* Video player */}
        <section className="max-w-6xl mx-auto w-full pb-6 px-4">
          <div className="relative aspect-video rounded-lg overflow-hidden shadow-2xl bg-black">
            <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
              <ReactPlayer
                ref={playerRef}
                url={recording.videoUrl || ""}
                controls={true}
                width="100%"
                height="100%"
                playing={false}
                onPlay={() => setIsVideoStarted(true)}
                onDuration={(d) => setVideoDuration(d)}
                onProgress={({ playedSeconds }) => setVideoCurrentTime(playedSeconds)}
                config={{
                  file: {
                    attributes: {
                      controlsList: "nodownload",
                      onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
                      crossOrigin: "anonymous",
                    },
                    forceVideo: true,
                  },
                }}
              />
            </div>
            {/* Timestamp overlay */}
            {isVideoStarted && (
              <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-1.5 pointer-events-none z-10">
                <span className="text-[20px] text-white tabular-nums font-medium">
                  {formatTime(videoCurrentTime)}{" "}
                  <span className="text-white/40">/ {formatTime(videoDuration)}</span>
                </span>
              </div>
            )}
            {/* Placeholder when no video URL */}
            {!recording.videoUrl && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <p className="text-white/20 text-[15px]">Video preview will appear here</p>
              </div>
            )}
          </div>
        </section>

        {/* Download full recording */}
        <section className="max-w-6xl mx-auto w-full pb-6 px-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                window.open(`/api/admin/private-recordings/${recordingId}/download/video`, "_blank");
              }}
              className="flex items-center gap-1.5 text-[13px] text-white/30 hover:text-white/60 transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              Download video
            </button>
            <span className="text-white/10">|</span>
            <button
              onClick={() => {
                window.open(`/api/admin/private-recordings/${recordingId}/download/audio`, "_blank");
              }}
              className="flex items-center gap-1.5 text-[13px] text-white/30 hover:text-white/60 transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              Download audio
            </button>
          </div>
        </section>

        {/* Tab toggle + content */}
        <section className="max-w-6xl mx-auto w-full pb-12 px-4">
          {/* Segmented toggle */}
          <div ref={tabRef} className="flex items-center justify-center mb-6">
            <div className="inline-flex bg-white/[0.04] border border-white/[0.08] rounded-full p-1">
              <button
                onClick={() => switchTab("timestamps")}
                className={`px-5 py-2 rounded-full text-[14px] font-medium transition-colors ${
                  activeTab === "timestamps"
                    ? "bg-white text-black"
                    : "text-white/40 hover:text-white/70"
                }`}
              >
                Add Timestamps
              </button>
              <button
                onClick={() => switchTab("clips")}
                className={`px-5 py-2 rounded-full text-[14px] font-medium transition-colors ${
                  activeTab === "clips"
                    ? "bg-white text-black"
                    : "text-white/40 hover:text-white/70"
                }`}
              >
                Download Clips{clips.length > 0 ? ` (${clips.length})` : ""}
              </button>
            </div>
          </div>

          {activeTab === "timestamps" ? (
            /* --- Add Timestamps tab --- */
            <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6">
              <p className="text-sm text-white/40 mb-6">
                Mark up to 20 key moments from this recording. Drag to reorder.
              </p>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={keyMomentsUi.slice(0, visibleCount).map((km) => km.clientId)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-4">
                    {keyMomentsUi.slice(0, visibleCount).map((km, index) => (
                      <SortableKeyMoment
                        key={km.clientId}
                        km={km}
                        index={index}
                        onTimestampChange={handleTimestampChange}
                        onTimestampBlur={handleTimestampBlur}
                        onTimestampEndChange={handleTimestampEndChange}
                        onTimestampEndBlur={handleTimestampEndBlur}
                        onEndModeChange={handleEndModeChange}
                        onLabelChange={handleLabelChange}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              {/* Add more timestamps button */}
              {visibleCount < MAX_TIMESTAMPS && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setVisibleCount((prev) => Math.min(prev + ADD_INCREMENT, MAX_TIMESTAMPS))}
                    className="flex items-center gap-2 text-[14px] text-white/40 hover:text-white/70 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Add more timestamps
                  </button>
                </div>
              )}

              {/* Save button */}
              <div className="flex justify-end mt-6">
                <Button
                  onClick={() => {
                    // Validate filled timestamps
                    const filled = keyMomentsUi.filter((km) => km.label.trim() !== "");
                    if (filled.length === 0) {
                      toast({ title: "Add at least one timestamp before saving" });
                      return;
                    }
                    let hasErrors = false;
                    const validated = keyMomentsUi.map((km) => {
                      if (km.label.trim() === "") return km;
                      const { normalized, error } = parseAndNormalizeTimestamp(km.timestamp);
                      if (error || !normalized) hasErrors = true;
                      let endError: string | undefined;
                      let normalizedEnd = km.timestampEnd;
                      if (km.endMode === "custom") {
                        if (!km.timestampEnd.trim()) {
                          endError = "End time is required for custom clips";
                          hasErrors = true;
                        } else {
                          const endResult = parseAndNormalizeTimestamp(km.timestampEnd);
                          normalizedEnd = endResult.normalized;
                          if (endResult.error) {
                            endError = endResult.error;
                            hasErrors = true;
                          } else if (normalized && normalizedEnd) {
                            const startSec = timestampToSeconds(normalized);
                            const endSec = timestampToSeconds(normalizedEnd);
                            if (endSec <= startSec) {
                              endError = "End time must be after start time";
                              hasErrors = true;
                            } else if (endSec - startSec > 60) {
                              endError = "Clip cannot exceed 60 seconds";
                              hasErrors = true;
                            }
                          }
                        }
                      }
                      return { ...km, timestamp: normalized, timestampError: error || (!normalized ? "Timestamp is required" : undefined), timestampEnd: normalizedEnd, timestampEndError: endError };
                    });
                    if (hasErrors) {
                      setKeyMomentsUi(validated);
                      return;
                    }
                    setKeyMomentsUi(validated);

                    // Build timestamps payload
                    const timestamps = filled.map((km, idx) => ({
                      startTime: km.timestamp,
                      endTime: km.endMode === "custom" ? km.timestampEnd : null,
                      durationType: km.endMode,
                      trackTitle: km.label,
                      sortOrder: idx,
                    }));

                    saveTimestampsMutation.mutate(timestamps);
                  }}
                  disabled={saveTimestampsMutation.isPending}
                  className="bg-white hover:bg-white/90 text-black font-medium px-8"
                >
                  {saveTimestampsMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Save
                </Button>
              </div>
            </div>
          ) : (
            /* --- Download Clips tab --- */
            <div className="space-y-4">
              {clips.length > 0 ? (
                <>
                  {/* Success banner */}
                  <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-4 py-3 text-[14px] text-center">
                    Timestamps saved. Clips are being generated from your recording.
                  </div>

                  <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold">Clip Generator</h3>
                        <p className="text-sm text-white/30 mt-0.5">
                          {clips.filter((c: any) => c.status === "done").length} of {clips.length} clips ready
                        </p>
                      </div>
                      <Button
                        onClick={() => bundleMutation.mutate()}
                        disabled={bundleMutation.isPending || clips.filter((c: any) => c.status === "done").length === 0}
                        className="bg-white hover:bg-white/90 text-black font-medium px-6"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download All ZIP
                      </Button>
                    </div>

                    {/* Bundle status */}
                    {bundle && (
                      <div className="mb-5 flex items-center gap-3">
                        <span className="text-[13px] text-white/30">ZIP Bundle:</span>
                        {getClipStatusBadge(bundle.status)}
                        {bundle.status === "done" && bundle.id && (
                          <a href={`/api/admin/recording-bundles/${bundle.id}/download`} target="_blank" rel="noreferrer" className="text-[13px] text-white/50 hover:text-white/80 underline underline-offset-2 transition-colors">
                            Download ZIP
                          </a>
                        )}
                      </div>
                    )}

                    {/* Clip table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/[0.08] text-[12px] text-white/30 uppercase tracking-wider">
                            <th className="text-left py-2.5 pr-4">#</th>
                            <th className="text-left py-2.5 pr-4">Start</th>
                            <th className="text-left py-2.5 pr-4">Duration</th>
                            <th className="text-left py-2.5 pr-4">Track</th>
                            <th className="text-left py-2.5 pr-4">Status</th>
                            <th className="text-left py-2.5 pr-4"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {clips.map((clip: any, idx: number) => (
                            <tr key={clip.id} className="border-b border-white/[0.04] last:border-b-0">
                              <td className="py-3.5 pr-4 text-white/20 tabular-nums">{idx + 1}</td>
                              <td className="py-3.5 pr-4 font-mono text-white/80 tabular-nums">{clip.startTime}</td>
                              <td className="py-3.5 pr-4 text-white/40">{clip.durationType || `${clip.durationSeconds}s`}</td>
                              <td className="py-3.5 pr-4 text-white/80 max-w-xs truncate">{clip.trackTitle}</td>
                              <td className="py-3.5 pr-4">
                                <div className="flex items-center gap-2">
                                  {getClipStatusBadge(clip.status)}
                                  {clip.status === "failed" && (
                                    <span title={clip.errorMessage || "Clip generation failed"}>
                                      <AlertCircle className="h-4 w-4 text-red-400" />
                                    </span>
                                  )}
                                </div>
                                {clip.status === "failed" && clip.errorMessage && (
                                  <p className="mt-1.5 max-w-md text-[12px] leading-snug text-red-400/80 break-words">
                                    {clip.errorMessage}
                                  </p>
                                )}
                              </td>
                              <td className="py-3.5 pr-4">
                                {clip.status === "done" && clip.previewUrl && (
                                  <button
                                    onClick={() => window.open(clip.previewUrl, "_blank")}
                                    className="flex items-center gap-1 text-[13px] text-white/30 hover:text-white/60 transition-colors"
                                  >
                                    <Play className="h-3.5 w-3.5" />
                                    Preview
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Add More Clips button */}
                    <div className="mt-5 flex justify-center">
                      <button
                        onClick={() => switchTab("timestamps")}
                        className="flex items-center gap-2 text-[14px] text-white/40 hover:text-white/70 transition-colors"
                      >
                        <Plus className="h-4 w-4" />
                        Add More Clips
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                /* Empty state — no clips yet */
                <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-12">
                  <div className="flex flex-col items-center justify-center text-center">
                    <div className="h-12 w-12 rounded-full bg-white/[0.04] flex items-center justify-center mb-4">
                      <Download className="h-5 w-5 text-white/20" />
                    </div>
                    <p className="text-[15px] text-white/40 mb-1">No clips yet</p>
                    <p className="text-[13px] text-white/20 mb-5">Add timestamps to generate downloadable clips from your recording.</p>
                    <Button
                      onClick={() => switchTab("timestamps")}
                      className="bg-white hover:bg-white/90 text-black font-medium px-6"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Timestamps
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </main>

      {/* Share modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Share this recording</DialogTitle>
            <DialogDescription>
              Send this link to the artist so they can review the recording, add timestamps, or download their set.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Shareable link (only shown after creation) */}
            {shareUrl && (
              <div
                onClick={() => {
                  navigator.clipboard.writeText(shareUrl);
                  setLinkCopied(true);
                  setTimeout(() => setLinkCopied(false), 2000);
                }}
                className="flex items-center gap-2 bg-[#1a1a1a] border border-white/10 rounded-md px-3 py-2.5 cursor-pointer hover:border-white/20 transition-colors overflow-hidden"
              >
                <span className="text-[13px] text-white/60 break-all flex-1">{shareUrl}</span>
                {linkCopied ? (
                  <Check className="h-4 w-4 text-emerald-400 shrink-0" />
                ) : (
                  <Copy className="h-4 w-4 text-white/40 shrink-0" />
                )}
              </div>
            )}

            {linkCopied && (
              <p className="text-[13px] text-emerald-400 text-center">Copied to clipboard</p>
            )}

            <p className="text-[13px] text-white/30">
              Anyone with this link will be able to view and download the recording.
            </p>

            {/* Download permission checkboxes */}
            <div className="space-y-3 pt-1">
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={enableVideoDownload}
                  onCheckedChange={(checked) => setEnableVideoDownload(checked === true)}
                  className="border-white/20 data-[state=checked]:bg-white data-[state=checked]:text-black"
                />
                <span className="text-[14px] text-white/60">Enable Video Download</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={enableAudioDownload}
                  onCheckedChange={(checked) => setEnableAudioDownload(checked === true)}
                  className="border-white/20 data-[state=checked]:bg-white data-[state=checked]:text-black"
                />
                <span className="text-[14px] text-white/60">Enable Audio Download</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={enableTimestampDownloads}
                  onCheckedChange={(checked) => setEnableTimestampDownloads(checked === true)}
                  className="border-white/20 data-[state=checked]:bg-white data-[state=checked]:text-black"
                />
                <span className="text-[14px] text-white/60">Enable Timestamp Downloads</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            {!shareUrl ? (
              <Button
                onClick={() => shareLinkMutation.mutate()}
                disabled={shareLinkMutation.isPending}
                className="bg-white hover:bg-white/90 text-black font-medium px-6"
              >
                {shareLinkMutation.isPending ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" />Creating...</>
                ) : (
                  "Create & Copy Link"
                )}
              </Button>
            ) : permissionsChanged ? (
              <Button
                onClick={() => shareLinkMutation.mutate()}
                disabled={shareLinkMutation.isPending}
                className="bg-white hover:bg-white/90 text-black font-medium px-6"
              >
                {shareLinkMutation.isPending ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" />Updating...</>
                ) : (
                  "Update & Copy Link"
                )}
              </Button>
            ) : (
              <Button
                onClick={() => setShowShareModal(false)}
                className="bg-white hover:bg-white/90 text-black font-medium px-6"
              >
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
